import * as React from 'react';
import { Banknote, Copy, Check } from 'lucide-react';
import { Card } from '../primitives/Card';
import { cn } from '../lib/cn';

/**
 * EftBankDetailsCard — bank deposit instructions for an unpaid invoice.
 *
 * Phase: shipped 2026-05-05 as the interim payment surface while PayFast
 * integration is deferred (see _bmad-output/CPANEL_AND_SITE_BUILDER_BRD.md
 * §5.3 + LD-9/LD-10). Reads bank details from siteConfig.bank — keep this
 * component dumb and pass the values in.
 *
 * Reference field highlights the invoice number and offers copy-to-clipboard
 * because mistyped references are the #1 reason EFT settlement gets stuck.
 */

interface EftBankDetailsCardProps {
  invoiceNumber: string;
  amountDisplay: string;
  bankName: string;
  accountName: string;
  accountNumber: string;
  branchCode: string;
  className?: string;
}

function EftBankDetailsCard({
  invoiceNumber,
  amountDisplay,
  bankName,
  accountName,
  accountNumber,
  branchCode,
  className,
}: EftBankDetailsCardProps) {
  const [copied, setCopied] = React.useState(false);

  async function copyReference() {
    try {
      await navigator.clipboard.writeText(invoiceNumber);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard blocked — silent fail; user can still type the reference manually.
    }
  }

  return (
    <Card interactive={false} className={className}>
      <header className="flex items-center gap-2 border-b border-border px-6 py-3">
        <Banknote className="size-4 text-primary" aria-hidden />
        <h3 className="text-sm font-semibold text-navy-primary">Pay by EFT</h3>
      </header>

      <dl className="grid grid-cols-3 gap-x-3 gap-y-2 px-6 py-5 text-sm">
        <dt className="text-muted-foreground">Bank</dt>
        <dd className="col-span-2 text-navy-primary">{bankName}</dd>

        <dt className="text-muted-foreground">Account name</dt>
        <dd className="col-span-2 text-navy-primary">{accountName}</dd>

        <dt className="text-muted-foreground">Account no.</dt>
        <dd className="col-span-2 text-navy-primary tabular-nums">{accountNumber}</dd>

        <dt className="text-muted-foreground">Branch code</dt>
        <dd className="col-span-2 text-navy-primary tabular-nums">{branchCode}</dd>

        <dt className="text-muted-foreground">Reference</dt>
        <dd className="col-span-2">
          <button
            type="button"
            onClick={copyReference}
            className={cn(
              'inline-flex items-center gap-1.5 rounded border border-primary/30 bg-primary/10 px-2 py-0.5',
              'font-mono text-xs font-semibold text-navy-primary tabular-nums',
              'transition-colors hover:bg-primary/15'
            )}
            aria-label={`Copy invoice reference ${invoiceNumber}`}
            title="Copy reference"
          >
            <span>{invoiceNumber}</span>
            {copied ? (
              <Check className="size-3 text-success" aria-hidden />
            ) : (
              <Copy className="size-3 text-muted-foreground" aria-hidden />
            )}
          </button>
        </dd>
      </dl>

      <footer className="border-t border-border bg-muted/30 px-6 py-3 text-xs text-muted-foreground">
        Pay <span className="font-medium text-navy-primary">{amountDisplay}</span> using
        the invoice number as your reference. Settlement appears in your account
        within 1 business day.
      </footer>
    </Card>
  );
}

export { EftBankDetailsCard };
export type { EftBankDetailsCardProps };
